// OrdersPage.jsx - Complete implementation with export functionality
import React, { useState, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Card,
  CardHeader,
  CardContent,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Menu,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import { useQuery } from '@tanstack/react-query';

// Import icons
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SummarizeIcon from '@mui/icons-material/Summarize';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfMonth, endOfMonth } from 'date-fns';

import heLocale from 'date-fns/locale/he';

// Import the export dialog
import ExportAllOrderItemsDialog from './dialogs/ExportAllOrderItemsDialog';
import MobileDialog from './dialogs/MobileDialog';
import OrderStatusDialog from './dialogs/OrderStatusDialog';

import ResponsiveDataView from '../components/ResponsiveDataView';
import ResponsiveToolbar, { ToolbarActions } from '../components/ResponsiveToolbar';
import PrintIcon from '@mui/icons-material/Print';
import { openPrintWindow } from '../utils/printUtils';
import { buildOrdersPrintHTML } from '../utils/orderPrintUtils';
import { useIsPhone } from '../hooks/useIsMobile';

// Theme setup
const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
          },
        },
      },
    },
  },
});

// Custom toolbar component
function CustomToolbar({ onRefresh }) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Tooltip title="רענן">
        <IconButton onClick={onRefresh}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </GridToolbarContainer>
  );
}

// Fetch order details for the expandable panel
const fetchOrderDetails = async (orderId) => {
  try {
    const response = await API.get(`/order/?OrderID=${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};

// Order detail panel component - simplified to show only products
// Update the OrderDetailPanel component (around line 80-200 in OrdersPage.jsx)
function OrderDetailPanel({ orderId, generalSchema, orderItemFields, orderItemFieldsSchema }) {
  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';
  
  const { data: orderDetails, isLoading, isError } = useQuery({
    queryKey: ['orderDetail', orderId],
    queryFn: () => fetchOrderDetails(orderId),
    enabled: !!orderId,
    staleTime: 5 * 60 * 1000,
  });

  // Get storage configuration from user context
  const { userProfile } = useUser();
  const role = userProfile?.role;
  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled || false;

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (isError || !orderDetails) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        שגיאה בטעינת פרטי ההזמנה
      </Alert>
    );
  }

  const hasItems = orderDetails.items && orderDetails.items.length > 0;

  if (!hasItems) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        אין {productPluralLabel} בהזמנה זו
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell align="center" width="50px">#</TableCell>
              <TableCell>שם {productLabel}</TableCell>
              <TableCell align="center">כמות</TableCell>
              {storageEnabled && <TableCell align="center">מחסן</TableCell>}
              <TableCell align="right">מחיר יחידה</TableCell>
              <TableCell align="right">סה"כ</TableCell>
              {orderItemFields.includes("Status") && <TableCell align="center">סטטוס</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {orderDetails.items.map((item, index) => (
              <TableRow key={item.OrderItemID} hover>
              <TableCell align="center" width="50px" sx={{ fontWeight: 'bold' }}>
                {index + 1}
              </TableCell>
                <TableCell>{item.ProductName}</TableCell>
                <TableCell align="center">{item.Quantity}</TableCell>
                {storageEnabled && (
                  <TableCell align="center">
                    {item.StorageName || 'ברירת מחדל'}
                  </TableCell>
                )}
                <TableCell align="right">
                  {item.PriceOverridden && item.OriginalPrice ? (
                    <Box sx={{ display:'flex', alignItems:'center', justifyContent: 'flex-end', direction:'ltr' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}
                      >
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(item.OriginalPrice)}
                      </Typography>
                      <Typography>
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(item.UnitPrice)}
                      </Typography>
                    </Box>
                  ) : (
                    new Intl.NumberFormat('he-IL', {
                      style: 'currency',
                      currency: 'ILS',
                    }).format(item.UnitPrice)
                  )}
                </TableCell>
                <TableCell align="right">
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(item.Subtotal)}
                </TableCell>
                {orderItemFields.includes("Status") && (
                  <TableCell align="center">
                    <Chip
                      label={item.StatusDisplay || (item.Status === 'supplied' ? 'סופק' : 'לא סופק')}
                      sx={{
                        backgroundColor: item.Status === 'supplied' ? '#e8f5e9' : '#ffebee',
                        color: item.Status === 'supplied' ? '#4caf50' : '#f44336',
                        borderColor: item.Status === 'supplied' ? '#4caf50' : '#f44336',
                        fontWeight: 'medium',
                        fontSize: '0.75rem',
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
            
            {/* Subtotal Row - Update colspan */}
            {orderDetails.Subtotal !== undefined && orderDetails.Subtotal !== orderDetails.TotalAmount && (
              <TableRow>
                <TableCell 
                  colSpan={storageEnabled ? 5 : 4} 
                  align="right" 
                  sx={{ fontWeight: 'bold' }}
                >
                  סה"כ לפני הנחה:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(orderDetails.Subtotal || 0)}
                </TableCell>
                {orderItemFields.includes("Status") && <TableCell />}
              </TableRow>
            )}
            
            {/* Discount Row - Update colspan */}
            {(orderDetails.DiscountAmount > 0 || orderDetails.DiscountPercentage > 0) && (
              <TableRow>
                <TableCell
                  colSpan={storageEnabled ? 5 : 4}
                  align="right"
                  sx={{ fontWeight: 'bold' }}
                >
                  הנחה{orderDetails.DiscountPercentage > 0 ? ` (${orderDetails.DiscountPercentage}%)` : ''}:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  -{new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(orderDetails.DiscountAmount || 0)}
                </TableCell>
                {orderItemFields.includes("Status") && <TableCell />}
              </TableRow>
            )}

            {/* Tax Row - Included */}
            {orderDetails.IsTaxable && orderDetails.IsTaxIncluded && orderDetails.TaxAmount > 0 && (
              <TableRow>
                <TableCell
                  colSpan={storageEnabled ? 5 : 4}
                  align="right"
                >
                  <Typography variant="body2" color="text.secondary">
                    מזה מע"מ ({orderDetails.TaxRate || 18}%):
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {new Intl.NumberFormat('he-IL', {
                      style: 'currency',
                      currency: 'ILS',
                    }).format(orderDetails.TaxAmount || 0)}
                  </Typography>
                </TableCell>
                {orderItemFields.includes("Status") && <TableCell />}
              </TableRow>
            )}

            {/* Tax Row - Excluded */}
            {orderDetails.IsTaxable && !orderDetails.IsTaxIncluded && orderDetails.TaxAmount > 0 && (
              <TableRow>
                <TableCell
                  colSpan={storageEnabled ? 5 : 4}
                  align="right"
                  sx={{ fontWeight: 'bold' }}
                >
                  מע"מ ({orderDetails.TaxRate || 18}%):
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  +{new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(orderDetails.TaxAmount || 0)}
                </TableCell>
                {orderItemFields.includes("Status") && <TableCell />}
              </TableRow>
            )}

            {/* Total Row - Update colspan */}
            <TableRow>
              <TableCell
                colSpan={storageEnabled ? 5 : 4}
                align="right"
                sx={{ fontWeight: 'bold' }}
              >
                סה"כ לתשלום:
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {new Intl.NumberFormat('he-IL', {
                  style: 'currency',
                  currency: 'ILS',
                }).format(orderDetails.TotalAmount || 0)}
              </TableCell>
              {orderItemFields.includes("Status") && <TableCell />}
            </TableRow>

            {/* Profit Summary Row — managers only */}
            {role === 'manager' && orderDetails.items && orderDetails.items.some(item => item.ProductCostPrice != null) && (() => {
              const itemsWithCost = orderDetails.items.filter(item => item.ProductCostPrice != null);
              const totalCost = itemsWithCost.reduce((sum, item) => sum + (parseFloat(item.ProductCostPrice) * parseFloat(item.Quantity)), 0);
              const totalRevenue = itemsWithCost.reduce((sum, item) => sum + (parseFloat(item.UnitPrice) * parseFloat(item.Quantity)), 0);
              const totalProfit = totalRevenue - totalCost;
              const profitPct = totalCost > 0 ? ((totalProfit / totalCost) * 100) : 0;
              const fmt = (v) => new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(v);
              return (
                <TableRow sx={{ backgroundColor: totalProfit >= 0 ? '#f1f8e9' : '#fce4ec' }}>
                  <TableCell
                    colSpan={storageEnabled ? 5 : 4}
                    align="right"
                    sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
                  >
                    רווח: {fmt(totalProfit)} ({profitPct.toFixed(1)}%)
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                    עלות: {fmt(totalCost)}
                  </TableCell>
                  {orderItemFields.includes("Status") && <TableCell />}
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Order Notes */}
      {orderDetails.Notes && (
        <Paper sx={{ mt: 2, p: 2, boxShadow: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>הערות</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{orderDetails.Notes}</Typography>
        </Paper>
      )}
    </Box>
  );
}

// Paginated fetch function for main list
const fetchPaginatedOrders = async ({
  page = 1,
  pageSize = 25,
  filters = {},
  ordering = '-OrderDate'
}) => {
  try {
    const params = new URLSearchParams({
      page: page,
      page_size: pageSize,
      ordering: ordering,
    });

    if (filters.Status) params.append('Status', filters.Status);
    if (filters.ClientBusinessID) params.append('ClientBusinessID', filters.ClientBusinessID);
    if (filters.OrderID) params.append('OrderID', filters.OrderID);
    if (filters.OrderNumber) params.append('order_number', filters.OrderNumber);

    // Order date parameters
    if (filters.startDate instanceof Date && !isNaN(filters.startDate)) {
      params.append('start_date', format(filters.startDate, 'yyyy-MM-dd'));
    }
    if (filters.endDate instanceof Date && !isNaN(filters.endDate)) {
      params.append('end_date', format(filters.endDate, 'yyyy-MM-dd'));
    }

    // Due date parameters
    if (filters.dueDateStart instanceof Date && !isNaN(filters.dueDateStart)) {
      params.append('due_date_start', format(filters.dueDateStart, 'yyyy-MM-dd'));
    }
    if (filters.dueDateEnd instanceof Date && !isNaN(filters.dueDateEnd)) {
      params.append('due_date_end', format(filters.dueDateEnd, 'yyyy-MM-dd'));
    }

    const queryString = params.toString();
    const url = `/orders/list/?${queryString}`;

    const response = await API.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching paginated orders:', error);
    throw error;
  }
};

// Helper function to convert grid sortModel to API ordering parameter
const getOrderingFromSortModel = (sortModel) => {
  if (!sortModel || sortModel.length === 0) {
    return '-OrderDate';  // Default: newest first
  }
  const { field, sort } = sortModel[0];
  return sort === 'desc' ? `-${field}` : field;
};

// Fetch order summary (for totals with applied filters)
const fetchOrderSummary = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.Status) params.append('Status', filters.Status);
    if (filters.ClientBusinessID) params.append('ClientBusinessID', filters.ClientBusinessID);
    if (filters.OrderID) params.append('OrderID', filters.OrderID);
    if (filters.OrderNumber) params.append('order_number', filters.OrderNumber);

    // Order date parameters
    if (filters.startDate instanceof Date && !isNaN(filters.startDate)) {
      params.append('start_date', format(filters.startDate, 'yyyy-MM-dd'));
    }
    if (filters.endDate instanceof Date && !isNaN(filters.endDate)) {
      params.append('end_date', format(filters.endDate, 'yyyy-MM-dd'));
    }

    // Due date parameters
    if (filters.dueDateStart instanceof Date && !isNaN(filters.dueDateStart)) {
      params.append('due_date_start', format(filters.dueDateStart, 'yyyy-MM-dd'));
    }
    if (filters.dueDateEnd instanceof Date && !isNaN(filters.dueDateEnd)) {
      params.append('due_date_end', format(filters.dueDateEnd, 'yyyy-MM-dd'));
    }

    const queryString = params.toString();
    const url = queryString ? `/orders/summary/?${queryString}` : '/orders/summary/';

    const response = await API.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching order summary:', error);
    return { total_orders: 0, total_amount: 0 };
  }
};

// Fetch orders for autocomplete with server-side search
const fetchOrdersAutocomplete = async (searchTerm = '') => {
  try {
    const params = {
      light: true,  // Use light mode for better performance
      limit: 100    // Limit results for autocomplete
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    const response = await API.get('/orders/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching orders for autocomplete:', error);
    return [];
  }
};

// Fetch clients for dropdown with server-side search
const fetchClients = async (searchTerm = '') => {
  try {
    const params = {
      light: true,
      limit: 100  // Always show 100 clients for consistency
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    const response = await API.get('/clients/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching clients:', error);
    return [];
  }
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const role = userProfile?.role;
  // Bulk-print is desktop-only — the checkbox column and print button
  // would crowd the mobile card view, and the printout is intended for
  // a real printer connected to the desktop machine.
  const isPhone = useIsPhone();

  const getThirtyDaysAgo = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29); // -29 because we want to include today (for 30 total days)
    return thirtyDaysAgo;
  };

  const getOneWeekAgo = () => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return oneWeekAgo;
  };

  const getToday = () => {
    return new Date(); // Today's date
  };

  // State for applied filters (what is sent to the API)
  const [appliedFilters, setAppliedFilters] = useState({
    Status: '',
    ClientBusinessID: '',
    OrderID: '',
    OrderNumber: '',
    startDate: getOneWeekAgo(),
    endDate: getToday(),
    dueDateStart: null,
    dueDateEnd: null,
  });

  // State for filter inputs (what user selects in the UI)
  const [filterInputs, setFilterInputs] = useState({
    Status: '',
    orderIdInput: '',
    startDate: getOneWeekAgo(),
    endDate: getToday(),
    dueDateStart: null,
    dueDateEnd: null,
  });
  
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [orderNumberInput, setOrderNumberInput] = useState('');
  const orderNumberInputRef = useRef('');  // Track current input without re-renders
  const [orderIdForFiltering, setOrderIdForFiltering] = useState('');
  const [orderAutocompleteKey, setOrderAutocompleteKey] = useState(0);  // Used to force reset Autocomplete
  const [exportAllItemsDialogOpen, setExportAllItemsDialogOpen] = useState(false);

  // Quick-actions 3-dot menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuOrder, setActionMenuOrder] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);

  const handleActionMenuOpen = (event, order) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuOrder(order);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  const handleQuickAction = (type) => {
    setActionMenuAnchor(null);
    if (type === 'status') setStatusDialogOpen(true);
  };

  // Bulk-print: row IDs the user has checked. Each ID is an OrderID.
  // Resets only when the user explicitly unchecks rows or refreshes the page —
  // we deliberately don't clear on filter/page change so the user can build a
  // selection across multiple result pages.
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [isBulkPrinting, setIsBulkPrinting] = useState(false);
  const [bulkPrintError, setBulkPrintError] = useState('');

  // Debounced autocomplete search state for clients
  const [debouncedAutocompleteSearch, setDebouncedAutocompleteSearch] = useState('');
  const autocompleteTimerCleanup = useRef(null);

  // Debounced autocomplete search state for orders
  const [debouncedOrderSearch, setDebouncedOrderSearch] = useState('');
  const orderAutocompleteTimerCleanup = useRef(null);

  // Pagination state (server-side)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Sorting state
  const [ordering, setOrdering] = useState('-OrderDate');

  // Ref for scrolling to table when pagination changes
  const tableContainerRef = useRef(null);

  // UI config
  const uiConfig = userProfile?.ui_config || {};
  const allowOrderManagement = uiConfig.allow_order_management === true;
  const orderFields = uiConfig.order_fields || [];
  const orderFieldsSchema = uiConfig.order_fields_schema || {};
  const orderConfig = uiConfig.order_config || {};
  const orderItemFields = uiConfig.order_item_fields || [];
  const orderItemFieldsSchema = uiConfig.order_item_fields_schema || {};
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {}; 
  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';
  const orderLabel = generalSchema?.order?.label || 'הזמנה';
  const orderPluralLabel = generalSchema?.order?.plural_label || 'הזמנות';
  const newOrderLabel = generalSchema?.new_order?.label || 'הזמנה חדשה';
  const clientFieldsSchema = uiConfig.client_fields_schema || {};
  const ClientAllowedRoles = uiConfig.pages_configs?.ClientDetailPage?.allowed_roles || ["manager", "employee"];
  // Helper function to check if "Today" filter is active
  
  const isTodayFilterActive = () => {
    if (!filterInputs.dueDateStart || !filterInputs.dueDateEnd) {
      return false;
    }
    
    const today = new Date();
    const startDate = new Date(filterInputs.dueDateStart);
    const endDate = new Date(filterInputs.dueDateEnd);
    
    // Check if both dates are set to today (compare year, month, day only)
    return startDate.getFullYear() === today.getFullYear() &&
           startDate.getMonth() === today.getMonth() &&
           startDate.getDate() === today.getDate() &&
           endDate.getFullYear() === today.getFullYear() &&
           endDate.getMonth() === today.getMonth() &&
           endDate.getDate() === today.getDate();
  };

  // Helper function to set due date to today only - does NOT auto-apply filter
  const setDueDateToToday = () => {
    const today = new Date();
    
    // Clear order date filters and set both due date start and end to today
    setFilterInputs(prev => ({
      ...prev,
      startDate: null,
      endDate: null,
      dueDateStart: today,
      dueDateEnd: today,
    }));
    
    // No auto-apply - user needs to click the filter button
  };

  // Normalize filters for query key to ensure consistent serialization
  const normalizedFilters = useMemo(() => ({
    ...appliedFilters,
    startDate: appliedFilters.startDate instanceof Date ? appliedFilters.startDate.toISOString() : appliedFilters.startDate,
    endDate: appliedFilters.endDate instanceof Date ? appliedFilters.endDate.toISOString() : appliedFilters.endDate,
    dueDateStart: appliedFilters.dueDateStart instanceof Date ? appliedFilters.dueDateStart.toISOString() : appliedFilters.dueDateStart,
    dueDateEnd: appliedFilters.dueDateEnd instanceof Date ? appliedFilters.dueDateEnd.toISOString() : appliedFilters.dueDateEnd,
  }), [appliedFilters]);

  // Paginated query - fetches current page with full data
  const {
    data: paginatedData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: [
      'orders',
      'paginated',
      paginationModel.page,
      paginationModel.pageSize,
      normalizedFilters,
      ordering
    ],
    queryFn: () => fetchPaginatedOrders({
      page: paginationModel.page + 1,  // Convert 0-based to 1-based
      pageSize: paginationModel.pageSize,
      filters: appliedFilters,
      ordering: ordering,
    }),
    staleTime: 0,
    gcTime: 1000,
    refetchOnMount: 'always',
  });

  // Extract data from paginated response
  const ordersData = paginatedData?.results || [];
  const totalRows = paginatedData?.count || 0;

  // Scroll to table when page changes
  const isFirstMount = useRef(true);
  React.useEffect(() => {
    // Skip scroll on first mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    // Scroll to table when page changes (not pageSize)
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [paginationModel.page]);

  // Debounced callback for order autocomplete search
  const debouncedSetOrderSearch = React.useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedOrderSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // React Query for order autocomplete (server-side search)
  const {
    data: ordersAutocompleteData = [],
    isLoading: isLoadingOrders,
  } = useQuery({
    queryKey: ['orders', 'autocomplete', debouncedOrderSearch],
    queryFn: () => fetchOrdersAutocomplete(debouncedOrderSearch),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Debounced callback for autocomplete search
  const debouncedSetAutocompleteSearch = React.useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedAutocompleteSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // React Query for clients (server-side search)
  const {
    data: clientsData = [],
    isLoading: isLoadingClients,
  } = useQuery({
    queryKey: ['clients', 'autocomplete', debouncedAutocompleteSearch],
    queryFn: () => fetchClients(debouncedAutocompleteSearch),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Create autocomplete options with dummy "no results" record when empty (and user is searching)
  const clientAutocompleteOptions = React.useMemo(() => {
    if (clientsData.length === 0 && debouncedAutocompleteSearch) {
      return [{ ClientBusinessID: -1, ClientName: 'אין תוצאות', isNoResults: true }];
    }
    return clientsData;
  }, [clientsData, debouncedAutocompleteSearch]);

  // React Query for order summary (with same filters as paginated data)
  const {
    data: summaryData,
  } = useQuery({
    queryKey: ['orders', 'summary', normalizedFilters],
    queryFn: () => fetchOrderSummary(appliedFilters),
    enabled: role === 'manager',
    staleTime: 0,
    gcTime: 1000,
    refetchOnMount: 'always',
  });


  // Create order autocomplete options with "no results" fallback
  const orderAutocompleteOptions = useMemo(() => {
    if (ordersAutocompleteData.length === 0 && debouncedOrderSearch) {
      return [{ OrderID: -1, OrderNumber: 'אין תוצאות', isNoResults: true }];
    }
    return ordersAutocompleteData;
  }, [ordersAutocompleteData, debouncedOrderSearch]);

  // Generate order suggestions for backward compatibility
  const orderSuggestions = useMemo(() => {
    return orderAutocompleteOptions
      .filter(order => !order.isNoResults)
      .map(order => ({
        id: order.OrderID,
        label: `${order.OrderNumber || order.OrderID}`,
      }));
  }, [orderAutocompleteOptions]);

   const orderSummary = useMemo(() => {
    // Only show summary if user is a manager
    if (role !== 'manager' || !summaryData) {
      return null;
    }

    return {
      totalOrders: summaryData.total_orders || 0,
      totalAmount: summaryData.total_amount || 0
    };
  }, [summaryData, role]);

  // Handle sort model changes
  const handleSortModelChange = React.useCallback((newSortModel) => {
    const newOrdering = getOrderingFromSortModel(newSortModel);
    setOrdering(newOrdering);
    setPaginationModel(prev => ({ ...prev, page: 0 }));  // Reset to page 0 on sort change
  }, []);

  // Handle pagination model changes (page or pageSize)
  const handlePaginationModelChange = React.useCallback((newModel) => {
    setPaginationModel(prevModel => {
      // If pageSize changed, reset to page 0
      if (newModel.pageSize !== prevModel.pageSize) {
        return { page: 0, pageSize: newModel.pageSize };
      }
      return newModel;
    });
  }, []);

  // Handle filter input change
  const handleFilterInputChange = (field, value) => {
    setFilterInputs(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle date filter changes with mutual exclusivity
  const handleDateFilterChange = (filterType, newDate) => {
    // Check if changing order date or due date filters
    const isOrderDateFilter = filterType === 'startDate' || filterType === 'endDate';
    const isDueDateFilter = filterType === 'dueDateStart' || filterType === 'dueDateEnd';
    
    // Create new filter inputs with the updated value
    const newFilterInputs = {
      ...filterInputs,
      [filterType]: newDate,
    };
    
    // If changing order date filters, clear any due date filters
    if (isOrderDateFilter) {
      newFilterInputs.dueDateStart = null;
      newFilterInputs.dueDateEnd = null;
    }
    
    // If changing due date filters, clear any order date filters
    if (isDueDateFilter) {
      newFilterInputs.startDate = null;
      newFilterInputs.endDate = null;
    }
    
    // Update the state with new filter values
    setFilterInputs(newFilterInputs);
  };

  // Apply filters - only place that triggers refetch
  const handleApplyFilters = () => {
    // If user entered an order number, determine how to search
    let finalOrderId = orderIdForFiltering;
    let finalOrderNumber = '';
    const currentOrderInput = orderNumberInputRef.current;

    if (currentOrderInput && !orderIdForFiltering) {
      // Trim whitespace for exact matching
      const trimmedInput = currentOrderInput.trim();

      // Try to match the typed input against OrderNumber in suggestions
      const matchingOrder = orderSuggestions.find(
        order => order.label === trimmedInput
      );
      if (matchingOrder) {
        // Found exact match in suggestions - use its OrderID for precise matching
        finalOrderId = matchingOrder.id.toString();
        setOrderIdForFiltering(finalOrderId);
      } else {
        // Not found in suggestions - search by order_number parameter
        // Backend will filter by user's group automatically
        finalOrderNumber = trimmedInput;
      }
    }

    const newFilters = {
      Status: filterInputs.Status,
      OrderID: finalOrderId,
      OrderNumber: finalOrderNumber,
      ClientBusinessID: selectedClient?.ClientBusinessID || '',

      // Include all date filters
      startDate: filterInputs.startDate,
      endDate: filterInputs.endDate,
      dueDateStart: filterInputs.dueDateStart,
      dueDateEnd: filterInputs.dueDateEnd,
    };

    // If order number/ID is being used, clear other filters
    if ((finalOrderId || finalOrderNumber) && currentOrderInput) {
      newFilters.ClientBusinessID = '';
      newFilters.Status = '';
      newFilters.startDate = null;
      newFilters.endDate = null;
      newFilters.dueDateStart = null;
      newFilters.dueDateEnd = null;

      // Also update the UI filter inputs to reflect cleared filters
      setSelectedClient(null);
      setFilterInputs(prev => ({
        ...prev,
        Status: '',
        startDate: null,
        endDate: null,
        dueDateStart: null,
        dueDateEnd: null,
      }));
    }

    // Only one of OrderID/OrderNumber or ClientBusinessID should be active at a time
    if (newFilters.OrderID || newFilters.OrderNumber) {
      newFilters.ClientBusinessID = '';
    }

    setAppliedFilters(newFilters);
    setPaginationModel(prev => ({ ...prev, page: 0 }));  // Reset to page 0 when filters change
  };

  // Reset and fetch orders
  const resetAndFetchOrders = () => {
    // Reset all filters
    setOrderNumberInput('');
    orderNumberInputRef.current = '';
    setOrderIdForFiltering('');
    setSelectedClient(null);
    setDebouncedOrderSearch('');
    setOrderAutocompleteKey(prev => prev + 1);  // Force Autocomplete to reset

    setFilterInputs({
      Status: '',
      orderIdInput: '',
      startDate: null,
      endDate: null,
      dueDateStart: null,
      dueDateEnd: null,
    });

    setAppliedFilters({
      Status: '',
      ClientBusinessID: '',
      OrderID: '',
      OrderNumber: '',
      startDate: null,
      endDate: null,
      dueDateStart: null,
      dueDateEnd: null,
    });

    setPaginationModel({ page: 0, pageSize: 25 });  // Reset pagination
  };

  // Bulk print: fetch full details (with items + payments) for every
  // selected order, build a single combined HTML doc with one order per
  // page, hand it to openPrintWindow. We can't print the rows directly
  // since the list endpoint only returns order summary fields — items
  // come from /order/?OrderID=X.
  const handleBulkPrint = async () => {
    if (isBulkPrinting || selectedOrderIds.length === 0) return;
    setBulkPrintError('');
    setIsBulkPrinting(true);
    try {
      const orders = await Promise.all(
        selectedOrderIds.map((id) => fetchOrderDetails(id))
      );
      const html = buildOrdersPrintHTML(orders, {
        businessProfile: userProfile?.group?.business_profile || {},
        orderLabel,
        productLabel,
        productPluralLabel,
        orderPluralLabel,
        orderFields,
        orderFieldsSchema,
        orderItemFields,
        orderPrintItemFields:
          uiConfig.pages_configs?.OrderDetailPage?.print_item_fields || [],
        barcodePrintSymbology:
          userProfile?.group?.barcode_config?.print_symbology,
      });
      openPrintWindow(html);
    } catch (e) {
      setBulkPrintError('שגיאה בהדפסה — לא ניתן היה לטעון את כל ההזמנות');
    } finally {
      setIsBulkPrinting(false);
    }
  };

  // Check if any filters are currently applied
  const hasActiveFilters = () => {
    return (
      appliedFilters.Status ||
      appliedFilters.ClientBusinessID ||
      appliedFilters.OrderID ||
      appliedFilters.OrderNumber ||
      appliedFilters.startDate ||
      appliedFilters.endDate ||
      appliedFilters.dueDateStart ||
      appliedFilters.dueDateEnd
    );
  };

  // Handle client selection
  const handleClientSelect = (event, newValue) => {
    setSelectedClient(newValue);
    // Reset pagination immediately when selecting a client
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    // Clear order ID input when selecting a client
    setFilterInputs(prev => ({
      ...prev,
      orderIdInput: ''
    }));

    // Clear autocomplete search and timer when clearing selection
    if (!newValue) {
      setDebouncedAutocompleteSearch('');
      if (autocompleteTimerCleanup.current) {
        autocompleteTimerCleanup.current();
      }
    }
  };

  // Handle order ID selection
  const handleOrderSelect = (event, newValue) => {
    // Ignore dummy "no results" record
    if (newValue && typeof newValue === 'object' && newValue.isNoResults) {
      return;
    }

    if (newValue) {
      // Reset pagination immediately when selecting an order
      setPaginationModel(prev => ({ ...prev, page: 0 }));

      // Cancel any pending autocomplete search
      if (orderAutocompleteTimerCleanup.current) {
        orderAutocompleteTimerCleanup.current();
      }
      setDebouncedOrderSearch('');  // Stop any pending/future searches

      // If selecting from dropdown
      if (typeof newValue === 'object' && newValue.id) {
        setOrderNumberInput(newValue.label);
        orderNumberInputRef.current = newValue.label;
        setOrderIdForFiltering(newValue.id.toString());
      }
      // If it's just a string input
      else if (typeof newValue === 'string') {
        setOrderNumberInput(newValue);
        orderNumberInputRef.current = newValue;
        setOrderIdForFiltering('');
      }

      // Clear all other filters when selecting an order
      setSelectedClient(null);
      setFilterInputs(prev => ({
        ...prev,
        Status: '',
        startDate: null,
        endDate: null,
        dueDateStart: null,
        dueDateEnd: null,
      }));
    } else {
      // Handle clearing - restore default date filtering
      setOrderNumberInput('');
      orderNumberInputRef.current = '';
      setOrderIdForFiltering('');
      setDebouncedOrderSearch('');

      // Clear any pending timer
      if (orderAutocompleteTimerCleanup.current) {
        orderAutocompleteTimerCleanup.current();
      }

      // Restore default date filters (one week ago to today)
      setFilterInputs(prev => ({
        ...prev,
        startDate: getOneWeekAgo(),
        endDate: getToday(),
      }));
    }
  };

  // Handle order number input change (typing in real-time)
  const handleOrderInputChange = (event, newInputValue, reason) => {
    // Update ref (doesn't trigger re-render)
    orderNumberInputRef.current = newInputValue;

    // Only trigger search when user is actually typing (not on selection or reset)
    if (reason === 'input') {
      // Clear previous timer
      if (orderAutocompleteTimerCleanup.current) {
        orderAutocompleteTimerCleanup.current();
      }

      // Debounce the API call for autocomplete
      orderAutocompleteTimerCleanup.current = debouncedSetOrderSearch(newInputValue);
    }

    // Handle clearing the field (via clear button or backspace to empty)
    if (reason === 'clear' || (reason === 'input' && newInputValue === '')) {
      setOrderNumberInput('');
      setOrderIdForFiltering('');
      setDebouncedOrderSearch('');
      // Clear any pending timer
      if (orderAutocompleteTimerCleanup.current) {
        orderAutocompleteTimerCleanup.current();
      }
      setFilterInputs(prev => ({
        ...prev,
        startDate: getOneWeekAgo(),
        endDate: getToday(),
      }));
    }
  };

  // Handle new order button click
  const handleNewOrderClick = () => {
    // Navigate directly to the new order page without any checks
    navigate('/order/new');
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3e0', color: '#ff9800' };
      case 'processing':
        return { bg: '#e1f5fe', color: '#0288d1' };
      case 'completed':
        return { bg: '#e8f5e9', color: '#4caf50' };
      case 'cancelled':
        return { bg: '#ffebee', color: '#f44336' };
      default:
        return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  // Get status display name
  const getStatusName = (status) => {
    const statusOptions = orderFieldsSchema.Status?.options || [
      { value: 'pending', label: 'בתהליך' },
      { value: 'processing', label: 'בטיפול' },
      { value: 'completed', label: 'הושלם' },
      { value: 'cancelled', label: 'בוטל' },
    ];
    const option = statusOptions.find(opt => opt.value === status);
    return option?.label || status;
  };

  // Get payment status
  const getPaymentStatus = (totalAmount, paidAmount) => {
    const total = parseFloat(totalAmount) || 0;
    const paid = parseFloat(paidAmount) || 0;
    
    if (total === 0) return 'no_payment';
    if (paid >= total) return 'fully_paid';
    if (paid > 0) return 'partial_paid';
    return 'unpaid';
  };

  // Get payment status display
  const getPaymentStatusDisplay = (status) => {
    switch (status) {
      case 'fully_paid':
        return { label: 'שולם במלואו', bg: '#e8f5e9', color: '#4caf50' };
      case 'partial_paid':
        return { label: 'שולם חלקית', bg: '#e1f5fe', color: '#0288d1' };
      case 'unpaid':
        return { label: 'לא שולם', bg: '#ffebee', color: '#f44336' };
      case 'no_payment':
        return { label: 'ללא תשלום', bg: '#f5f5f5', color: '#757575' };
      default:
        return { label: 'לא ידוע', bg: '#f5f5f5', color: '#757575' };
    }
  };

  // Define columns for DataGrid
  const columns = [
    {
      field: 'OrderNumber',
      headerName: orderFieldsSchema.OrderNumber?.label || `מספר ${orderLabel}`,
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button
          variant="text"
          color="primary"
          onClick={() => navigate(`/order/${params.id}`, { state: { from: '/orders' } })}
          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: 'ClientName',
      headerName: clientFieldsSchema?.ClientName?.label || 'שם לקוח',
      flex: 2,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const canNavigateToClient = ClientAllowedRoles.includes(role);
        
        if (canNavigateToClient) {
          return (
            <Button
              variant="text"
              color="primary"
              onClick={() => navigate(`/client/${params.row.ClientBusinessID}`)}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {params.value || 'לא ידוע'}
            </Button>
          );
        } else {
          return (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.primary',
                textAlign: 'center',
                fontSize: '1.1rem'
              }}
            >
              {params.value || 'לא ידוע'}
            </Typography>
          );
        }
      },
    },
    {
      field: 'ClientBusinessID',
      headerName: 'מזהה לקוח',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
    },
    {
      field: 'OrderDate',
      headerName: orderFieldsSchema.OrderDate?.label || `תאריך ${orderLabel}`,
      flex: 1.5,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('he-IL');
      },
      sortComparator: (v1, v2) => {
        const date1 = v1 ? new Date(v1).getTime() : 0;
        const date2 = v2 ? new Date(v2).getTime() : 0;
        return date1 - date2;
      }
    },
    {
      field: 'Status',
      headerName: orderFieldsSchema.Status?.label || 'סטטוס',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const statusColors = getStatusColor(params.value);
        return (
          <Chip
            label={getStatusName(params.value)}
            sx={{
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              borderColor: statusColors.color,
              fontWeight: 'medium',
            }}
            variant="outlined"
            size="small"
          />
        );
      },
    },
    {
      field: 'TotalAmount',
      headerName: orderFieldsSchema.TotalAmount?.label || 'סכום כולל',
      flex: 1,
      minWidth: 130,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => {
        if (value === undefined || value === null) return '';
        return new Intl.NumberFormat('he-IL', {
          style: 'currency',
          currency: 'ILS',
        }).format(value);
      },
    },
    {
      field: 'PaidAmount',
      headerName: orderFieldsSchema.PaidAmount?.label || 'סכום ששולם',
      flex: 1,
      minWidth: 130,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      valueFormatter: (value) => {
        if (value === undefined || value === null) return '';
        return new Intl.NumberFormat('he-IL', {
          style: 'currency',
          currency: 'ILS',
        }).format(value);
      },
    },
    {
      field: 'PaymentStatus',
      headerName: 'מצב תשלום',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      valueGetter: (value, row) => {
        return getPaymentStatus(row.TotalAmount, row.PaidAmount);
      },
      renderCell: (params) => {
        const paymentStatus = getPaymentStatusDisplay(params.value);
        return (
          <Chip
            label={paymentStatus.label}
            sx={{
              backgroundColor: paymentStatus.bg,
              color: paymentStatus.color,
              borderColor: paymentStatus.color,
              fontWeight: 'medium',
            }}
            variant="outlined"
            size="small"
          />
        );
      },
      sortComparator: (v1, v2) => {
        // Sort by payment status: unpaid, partial_paid, fully_paid
        const order = { unpaid: 0, partial_paid: 1, fully_paid: 2, no_payment: 3 };
        return order[v1] - order[v2];
      },
    },
    {
      field: 'DueDate',
      headerName: orderFieldsSchema.DueDate?.label || 'תאריך אספקה',
      flex: 1.5,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      valueFormatter: (value) => {
        if (!value) return '–';
        return new Date(value).toLocaleDateString('he-IL');
      },
    },
  ];

  const FinalColumns = [];

  if (allowOrderManagement) {
    FinalColumns.push({
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleActionMenuOpen(e, params.row)}
          aria-label="פעולות מהירות"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    });
  }

  columns.forEach(col => {
    if (orderFields.includes(col.field)) {
      FinalColumns.push(col);
    }
    if (col.field == "ClientName" || col.field == "ClientBusinessID") {
      FinalColumns.push(col);
    }
  });

  const orderCardRenderer = (row) => {
    const total = parseFloat(row?.TotalAmount || '0');
    const formatted = new Intl.NumberFormat('he-IL', {
      style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(total);

    const statusLabels = {
      pending: 'בתהליך', processing: 'בטיפול',
      completed: 'הושלם', cancelled: 'בוטל',
    };
    const statusColors = {
      pending: '#ff9800', processing: '#1976d2',
      completed: '#4caf50', cancelled: '#f44336',
    };
    const status = row?.Status || '';
    const dateStr = row?.OrderDate ? new Date(row.OrderDate).toLocaleDateString('he-IL') : '';

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.ClientName || ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              #{row.OrderNumber} {dateStr && `· ${dateStr}`}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'left', flexShrink: 0, ml: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {formatted}
            </Typography>
            {status && (
              <Typography variant="caption" sx={{
                px: 1, py: 0.25, borderRadius: 1,
                backgroundColor: (statusColors[status] || '#9e9e9e') + '20',
                color: statusColors[status] || '#9e9e9e',
                fontWeight: 'bold',
              }}>
                {statusLabels[status] || status}
              </Typography>
            )}
          </Box>
          {allowOrderManagement && (
            <IconButton
              size="small"
              onClick={(e) => handleActionMenuOpen(e, row)}
              aria-label="פעולות מהירות"
              sx={{ flexShrink: 0, ml: 0.5 }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <ThemeProvider theme={theme}>
        <div dir="rtl">
          <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: { xs: 1.5, sm: 3 } }}>
              <Typography variant="h4" component="h1" fontWeight="bold">
                ניהול {orderPluralLabel}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SummarizeIcon />}
                  onClick={() => setExportAllItemsDialogOpen(true)}
                  sx={{
                    borderColor: 'primary.main',
                    direction: 'ltr',
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      borderColor: 'primary.dark',
                    }
                  }}
                >
                  סיכום {productPluralLabel}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleNewOrderClick}
                  sx={{ direction: 'ltr', flex: { xs: 1, sm: 'none' } }}
                >
                  {newOrderLabel}
                </Button>
              </Box>
            </Box>

            {isError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error?.response?.data?.error || `שגיאה בטעינת ה${orderPluralLabel}. אנא נסה שוב.`}
              </Alert>
            )}

            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardHeader
                title={
                  <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                    רשימת {orderPluralLabel} {isFetching && !isLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
                  </Typography>
                }
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  py: 1.5,
                }}
              />
              <CardContent>
                {/* Filters Row - Ultra Compact */}
                <ResponsiveToolbar>
                  <Autocomplete
                    options={clientAutocompleteOptions}
                    getOptionLabel={(option) => {
                      if (option.isNoResults) return option.ClientName;
                      return `${option.ClientName} (${option.ClientBusinessID})`;
                    }}
                    getOptionDisabled={(option) => option.isNoResults === true}
                    value={selectedClient}
                    onChange={(event, newValue) => {
                      if (newValue && newValue.isNoResults) return;
                      handleClientSelect(event, newValue);
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (autocompleteTimerCleanup.current) {
                        autocompleteTimerCleanup.current();
                      }
                      autocompleteTimerCleanup.current = debouncedSetAutocompleteSearch(newInputValue);
                      if (newInputValue.trim() === '' && selectedClient) {
                        setSelectedClient(null);
                      }
                    }}
                    loading={isLoadingClients}
                    loadingText="טוען..."
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="לקוח"
                        placeholder="בחר לקוח"
                        size="small"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.ClientBusinessID === value?.ClientBusinessID}
                    sx={{ width: { xs: '100%', md: 250 }, minWidth: { xs: 0, md: 250 } }}
                  />

                  <Autocomplete
                    key={orderAutocompleteKey}
                    options={orderSuggestions}
                    getOptionLabel={(option) => {
                      if (typeof option === 'object' && option !== null) {
                        return option.label || '';
                      }
                      return option || '';
                    }}
                    getOptionDisabled={(option) => option.isNoResults === true}
                    value={orderNumberInput ? { label: orderNumberInput, id: orderIdForFiltering } : null}
                    onChange={handleOrderSelect}
                    onInputChange={handleOrderInputChange}
                    loading={isLoadingOrders}
                    loadingText="טוען..."
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`מספר ${orderLabel}`}
                        placeholder="הקלד מספר"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingOrders ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    freeSolo
                    sx={{ width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } }}
                  />

                  {orderFields.includes("Status") &&
                  <TextField
                    select
                    label="סטטוס"
                    size="small"
                    value={filterInputs.Status}
                    onChange={(e) => handleFilterInputChange('Status', e.target.value)}
                    sx={{ minWidth: { xs: 0, md: 140 } }}
                  >
                    <MenuItem value="">הכל</MenuItem>
                    {(orderFieldsSchema.Status?.options || [
                      { value: 'pending', label: 'בתהליך' },
                      { value: 'processing', label: 'בטיפול' },
                      { value: 'completed', label: 'הושלם' },
                      { value: 'cancelled', label: 'בוטל' },
                    ]).map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  }

                  <DatePicker
                    label="חודש"
                    value={selectedMonth}
                    onChange={(newDate) => {
                      if (newDate) {
                        setSelectedMonth(newDate);
                        handleDateFilterChange('startDate', startOfMonth(newDate));
                        handleDateFilterChange('endDate', endOfMonth(newDate));
                      }
                    }}
                    views={['month', 'year']}
                    slotProps={{
                      textField: { size: 'small', sx: { direction: 'ltr', width: { xs: '100%', md: 180 }, minWidth: { xs: 0, md: 180 } } }
                    }}
                  />
                  <DatePicker
                    label="מתאריך"
                    value={filterInputs.startDate}
                    onChange={(newDate) => { handleDateFilterChange('startDate', newDate); setSelectedMonth(null); }}
                    slotProps={{
                      textField: { size: 'small', sx: { direction: 'ltr', width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } } }
                    }}
                  />
                  <DatePicker
                    label="עד תאריך"
                    value={filterInputs.endDate}
                    onChange={(newDate) => { handleDateFilterChange('endDate', newDate); setSelectedMonth(null); }}
                    slotProps={{
                      textField: { size: 'small', sx: { direction: 'ltr', width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } } }
                    }}
                  />

                  {orderFields.includes("DueDate") && (
                    <DatePicker
                      label="מתאריך אספקה"
                      value={filterInputs.dueDateStart}
                      onChange={(newDate) => handleDateFilterChange('dueDateStart', newDate)}
                      slotProps={{
                        textField: { size: 'small', sx: { direction: 'ltr', width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } } }
                      }}
                    />
                  )}
                  {orderFields.includes("DueDate") && (
                    <DatePicker
                      label="עד תאריך אספקה"
                      value={filterInputs.dueDateEnd}
                      onChange={(newDate) => handleDateFilterChange('dueDateEnd', newDate)}
                      slotProps={{
                        textField: { size: 'small', sx: { direction: 'ltr', width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } } }
                      }}
                    />
                  )}

                  <ToolbarActions>
                    {orderFields.includes("DueDate") && (
                      <Button
                        variant={isTodayFilterActive() ? "contained" : "outlined"}
                        onClick={setDueDateToToday}
                      >
                        היום
                      </Button>
                    )}
                    {!isPhone && selectedOrderIds.length > 0 && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PrintIcon />}
                        onClick={handleBulkPrint}
                        disabled={isBulkPrinting}
                        sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
                      >
                        {isBulkPrinting
                          ? `טוען (${selectedOrderIds.length})...`
                          : `הדפס מסומנים (${selectedOrderIds.length})`}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<FilterAltIcon />}
                      onClick={handleApplyFilters}
                      disabled={isFetching}
                    >
                      {isFetching ? 'טוען...' : 'סנן'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={resetAndFetchOrders}
                      disabled={!hasActiveFilters()}
                    >
                      נקה
                    </Button>
                  </ToolbarActions>
                </ResponsiveToolbar>

                {bulkPrintError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setBulkPrintError('')}
                  >
                    {bulkPrintError}
                  </Alert>
                )}

                {/* Orders DataGrid */}
                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box
                    ref={tableContainerRef}
                    sx={{
                      width: '100%',
                      '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                        fontSize: '1.15rem',
                        whiteSpace: 'normal !important',  // ADD THIS - allows header text to wrap
                        lineHeight: '1.2',
                        overflow: 'visible',
                      },
                      '.MuiDataGrid-cell': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        fontSize: '1rem',
                      },
                    }}>
                    <ResponsiveDataView
                      rows={ordersData}
                      columns={FinalColumns}
                      getRowId={(row) => row.OrderID}
                      cardRenderer={orderCardRenderer}
                      onRowClick={(row) => navigate('/order/' + row.OrderID, { state: { from: '/orders' } })}
                      emptyMessage="אין תוצאות"
                      density="comfortable"

                      // Bulk-print selection. Desktop-only — on phones the
                      // ResponsiveDataView swaps to a card layout where a
                      // checkbox column doesn't fit, so we don't enable it.
                      // keepNonExistentRowsSelected: with server-side
                      // pagination the rows array changes per page; this
                      // flag tells DataGrid to preserve selected IDs that
                      // aren't in the current page so a user can build a
                      // selection across pages and print all of them.
                      checkboxSelection={!isPhone}
                      rowSelectionModel={selectedOrderIds}
                      onRowSelectionModelChange={(newSelection) =>
                        setSelectedOrderIds(newSelection)
                      }
                      keepNonExistentRowsSelected

                      // Server-side pagination
                      paginationMode="server"
                      paginationModel={paginationModel}
                      onPaginationModelChange={handlePaginationModelChange}
                      rowCount={totalRows}
                      pageSizeOptions={[25, 50, 100]}
                      pagination

                      // Server-side sorting
                      sortingMode="server"
                      onSortModelChange={handleSortModelChange}

                      autoHeight
                      disableSelectionOnClick
                      loading={isFetching}
                      localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                      slots={{
                        toolbar: () =>
                          <CustomToolbar
                            onRefresh={refetch}
                          />
                      }}
                      // Detail panel configuration
                      getDetailPanelContent={({ row }) => (
                        <OrderDetailPanel
                          orderId={row.OrderID}
                          generalSchema={generalSchema}
                          orderItemFields={orderItemFields}
                          orderItemFieldsSchema={orderItemFieldsSchema}
                        />
                      )}
                      getDetailPanelHeight={() => 'auto'}
                      // No-rows overlay
                      components={{
                        NoRowsOverlay: () => (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 5 }}>
                            <Typography color="text.secondary">אין {orderPluralLabel} להצגה</Typography>
                          </Box>
                        ),
                      }}
                      initialState={{
                        sorting: {
                          sortModel: [{ field: 'OrderDate', sort: 'desc' }],
                        },
                      }}
                      getRowClassName={(params) => {
                        if (params.row.Status === 'cancelled') return 'cancelled-row';
                        return '';
                      }}
                      sx={{
                        '& .cancelled-row': {
                          color: 'text.disabled',
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          borderTop: '1px solid rgba(224, 224, 224, 1)',
                        },
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
            {role === 'manager' && orderSummary && (
              <Box sx={{ mt: 3, mb: 2 }}>
                {/* Summary Title */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'right' }}>
                  סיכום {orderPluralLabel}:
                </Typography>
                
                {/* Summary Items */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-start', 
                  alignItems: 'center', 
                  gap: 3,
                  flexWrap: 'wrap' 
                }}>
                  {/* Total Orders Count */}
                  <Box sx={{ 
                    backgroundColor: '#e3f2fd', // Light blue
                    padding: '8px 16px',
                    borderRadius: 2,
                    border: '1px solid #90caf9',
                    display: 'inline-block'
                  }}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                      מספר {orderPluralLabel}:{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {orderSummary.totalOrders}
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Total Amount */}
                  <Box sx={{ 
                    backgroundColor: '#f3e5f5', // Light purple
                    padding: '8px 16px',
                    borderRadius: 2,
                    border: '1px solid #ce93d8',
                    display: 'inline-block'
                  }}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                      סכום כולל:{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(orderSummary.totalAmount)}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {/* Warning Dialog */}
            <MobileDialog
              open={warningDialogOpen}
              onClose={() => setWarningDialogOpen(false)}
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                <Typography>אזהרה</Typography>
              </DialogTitle>
              <DialogContent>
                <Typography>{dialogMessage}</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setWarningDialogOpen(false)}>סגור</Button>
                {dialogMessage.includes('לקוח') && (
                  <Button color="primary" onClick={() => navigate('/clients')}>
                    עבור ללקוחות
                  </Button>
                )}
                {dialogMessage.includes(productLabel) && (
                  <Button color="primary" onClick={() => navigate('/products')}>
                    עבור ל{productPluralLabel}
                  </Button>
                )}
              </DialogActions>
            </MobileDialog>
            
            {/* Export All Order Items Dialog */}
            <ExportAllOrderItemsDialog
              open={exportAllItemsDialogOpen}
              onClose={() => setExportAllItemsDialogOpen(false)}
              orderLabel={orderLabel}
              orderPluralLabel={orderPluralLabel}
              productLabel={productLabel}
              productPluralLabel={productPluralLabel}
            />

            {/* Quick-actions menu */}
            <Menu
              anchorEl={actionMenuAnchor}
              open={Boolean(actionMenuAnchor)}
              onClose={handleActionMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => handleQuickAction('status')}>שינוי סטטוס</MenuItem>
            </Menu>

            <OrderStatusDialog
              open={statusDialogOpen}
              onClose={() => setStatusDialogOpen(false)}
              order={actionMenuOrder}
              onSuccess={refetch}
              orderFieldsSchema={orderFieldsSchema}
              orderLabel={orderLabel}
            />
          </Container>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default OrdersPage;